import React, { useState, useCallback, useEffect } from "react";
import SearchModal from "../components/SearchModal";

export interface BitrixSearchModalProps {
  /** CSS selector for trigger element (string or array of strings) */
  triggerSelector?: string | string[];
  /** Placeholder text for search input */
  placeholder?: string;
  /** Modal title */
  title?: string;
  /** CSS class name */
  className?: string;
  /** Whether to auto-initialize on mount */
  autoInit?: boolean;
}

/**
 * BitrixSearchModal - Self-contained search modal for Bitrix integration
 *
 * Manages its own open/close state and automatically attaches click handler
 * to the specified trigger element.
 */
const BitrixSearchModal: React.FC<BitrixSearchModalProps> = ({
  triggerSelector,
  placeholder = "Что вы хотите найти?",
  title = "Поиск CarolinaShop",
  className = "",
  autoInit = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Get configuration from global object or props
  const config = window.LiveSearchConfig || {};
  const finalTriggerSelector =
    triggerSelector || config.triggerSelector || ".site_s1 .search-input";

  const openModal = useCallback(() => {
    setIsOpen(true);
    // Prevent body scroll
    document.body.style.overflow = "hidden";
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    // Restore body scroll
    document.body.style.overflow = "";
  }, []);

  // Initialize trigger click handler
  useEffect(() => {
    if (!autoInit) return;

    const handleTriggerClick = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      openModal();
    };

    // Convert to array of selectors
    const selectors = Array.isArray(finalTriggerSelector)
      ? finalTriggerSelector
      : [finalTriggerSelector];

    // Collect unique elements
    const elements = new Set<Element>();
    selectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((element) => elements.add(element));
    });

    if (elements.size === 0) {
      console.warn(`Live Search: No trigger elements found for selectors: ${selectors.join(", ")}`);
      return;
    }

    // Store original click handlers for each element
    const elementToOriginalClick = new Map<Element, EventListener | null>();

    // Add event listeners to all unique elements
    elements.forEach((element) => {
      // Store original click handler
      const originalClick = element.getAttribute("onclick") || (element as any).onclick;
      const originalListener =
        originalClick && typeof originalClick === "function"
          ? (originalClick as EventListener)
          : null;
      elementToOriginalClick.set(element, originalListener);

      // Add our click handler
      element.addEventListener("click", handleTriggerClick);

      // Preserve original handler if it exists
      if (originalListener) {
        element.addEventListener("click", originalListener);
      }
    });

    console.log(`Live Search: Trigger initialized on ${elements.size} element(s)`);

    return () => {
      // Clean up all event listeners
      elements.forEach((element) => {
        element.removeEventListener("click", handleTriggerClick);
        const originalListener = elementToOriginalClick.get(element);
        if (originalListener) {
          element.removeEventListener("click", originalListener);
        }
      });
    };
  }, [finalTriggerSelector, openModal, autoInit]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        closeModal();
      }
    };

    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isOpen, closeModal]);

  // Handle click outside to close modal
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const modalElement = document.querySelector(".search-modal-overlay");
      if (modalElement && event.target === modalElement) {
        closeModal();
      }
    };

    if (isOpen) {
      document.addEventListener("click", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, [isOpen, closeModal]);

  return (
    <SearchModal
      isOpen={isOpen}
      onClose={closeModal}
      placeholder={placeholder}
      title={title}
      className={className}
    />
  );
};

export default BitrixSearchModal;
